/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { withRouter } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { parseISO, format } from 'date-fns';
import { AnalyticsInstance } from '@ts/utils/analytics';
import actions from '@ts/core/actions';
import selectors from '@ts/core/selectors';
import PureHeader from '@ts/components/Header';
import routes from '@ts/containers/App/routes';

// import homeImage1 from '@ts/images/home-slide1.jpg';
import homeImage2 from '@ts/images/home-slide2.jpg';
import homeImage2WebP from '@ts/images/home-slide2.webp';
// import homeImage3 from '@ts/images/home-slide3.jpg';

import storiesImage from '@ts/images/stories-bg-0.5x.png';
import storiesImageWebP from '@ts/images/stories-bg-0.5x.webp';
import blogsImage from '@ts/images/blog-bg-0.5x.png';
import blogsImageWebP from '@ts/images/blog-bg-0.5x.webp';
import partnersImage from '@ts/images/partners-bg-0.5x.png';
import partnersImageWebP from '@ts/images/partners-bg-0.5x.webp';
import { IMAGES_DESTINATION_ROOT, IMAGES_ROOT } from '@ts/utils/constants';

const pageKeys = Object.keys(routes);
const defaults = {
    forceCarousel: false,
    forceSimplify: false,
    forceSimplifyMobile: false,
    images: undefined,
    navStatic: false,
    subtitle: '',
    title: '',
    unmount: false,
    partnersLink: true,
};

const commonKeys = [
    'destinationsAsObject',
    'categoriesAsObject',
    'wishList',
    'cartItems',
    'searchDestinations',
    'searchDateFrom',
    'searchDateTo',
    'searchDestination',
    'searchDestinationCategory',
    'cartIsSad',
    'cartIsOutdated',
    'checkoutPosition',
];

const dynamicKeys = {
    activityPage: ['is404', 'page'],
    activityReviewsPage: ['is404', 'page'],
    activityReviewPage: ['is404', 'page'],
    attractionsPage: ['is404', 'page'],
    blogPostPage: ['is404', 'page'],
    blogsDestinationPage: ['is404', 'page', 'params'],
    contentPage: ['is404', 'page'],
    destinationPage: ['is404', 'page', 'params'],
    itineraryPage: ['is404', 'page'],
    partnerPage: ['is404', 'page'],
    partnersDestinationPage: ['is404', 'page', 'params'],
    storiesDestinationPage: ['is404', 'page', 'params'],
    storyPage: ['is404', 'page'],
};

const dynamicReducerKeys = Object.keys(dynamicKeys);

export function getSubProps(props) {
    const {
        common: {
            destinationsAsObject,
            // categoriesAsObject,
            cartIsSad,
            cartIsOutdated,
            checkoutPosition,
        },
        state,
        match: { path },
    } = props;

    // Unmount Logic
    if (
        [
            routes.activityPage,
            routes.activityReviewsPage,
            routes.activityReviewPage,
            routes.attractionsPage,
            routes.blogPostPage,
            routes.blogsDestinationPage,
            routes.contentPage,
            routes.destinationPage,
            routes.itineraryPage,
            routes.partnerPage,
            routes.partnersDestinationPage,
            routes.storiesDestinationPage,
            routes.storyPage,
        ].includes(path)
    ) {
        const substate =
            state[
                {
                    [routes.activityPage]: 'activityPage',
                    [routes.activityReviewsPage]: 'activityReviewsPage',
                    [routes.activityReviewPage]: 'activityReviewPage',
                    [routes.attractionsPage]: 'attractionsPage',
                    [routes.blogPostPage]: 'blogPostPage',
                    [routes.blogsDestinationPage]: 'blogsDestinationPage',
                    [routes.contentPage]: 'contentPage',
                    [routes.destinationPage]: 'destinationPage',
                    [routes.itineraryPage]: 'itineraryPage',
                    [routes.partnerPage]: 'partnerPage',
                    [routes.partnersDestinationPage]: 'partnersDestinationPage',
                    [routes.storiesDestinationPage]: 'storiesDestinationPage',
                    [routes.storyPage]: 'storyPage',
                }[path]
            ];

        // unmount header is substate have 404 or error status
        if (substate.is404 || substate.page.isError) {
            return {
                unmount: true,
            };
        }
    }

    if (path === routes.storyPage) {
        const substate = state.storyPage;
        const page = substate.page.res;

        if (!page) {
            return {
                forceCarousel: true,
            };
        }

        const image = page.image
            ? `${IMAGES_ROOT}story/${page.image.id}/${page.image.slug}.jpg`
            : '';

        if (!image) {
            return defaults;
        }

        let date = page.updated_at;

        try {
            if (typeof date === 'number') {
                const parsedDate = parseISO(new Date(date).toISOString());
                date = format(parsedDate, 'MMMM do yyyy');
            }
        } catch {}

        return {
            title: page.title,
            subtitle: date,
            images: [image],
        };
    }

    if (path === routes.destinationPage) {
        const substate = state.destinationPage;
        const destination = substate.page.res;
        if (destination) {
            return {
                title: `${destination.name}, ${destination.state.name}`,
                subtitle: (
                    <React.Fragment>
                        Book{' '}
                        {substate.page.isLoaded ? (
                            substate.page.res.total_activities
                        ) : (
                            <FontAwesomeIcon spin fixedWidth icon={faSpinner} />
                        )}{' '}
                        Tours, Rentals & Attraction Tickets
                    </React.Fragment>
                ),
                images: [`${IMAGES_DESTINATION_ROOT}${destination.id}/${destination.slug}.jpg`],
            };
        }
    }

    // Custom View Logic
    switch (path) {
        case routes.partnersPage:
            return {
                title: 'Our Partners',
                subtitle: 'Experience the best',
                images: [partnersImage, partnersImageWebP],
            };
        case routes.destinationsPage:
            return { forceSimplifyMobile: true };
        case routes.reviewAddPage:
            return {
                partnersLink: false,
                forceSimplifyMobile: true,
            };
        case routes.voucherPage:
        case routes.orderPage:
            return { navStatic: true, forceSimplify: true };
        case routes.cartPage:
        case routes.checkoutPage:
            return {
                navStatic: true,
                forceSimplify: true,
                unmount: cartIsSad && !cartIsOutdated,
                checkoutPosition,
            };
        case routes.activityPage:
        case routes.activityReviewsPage:
            return {
                forceSimplifyMobile: true,
            };
        case routes.homePage:
            return {
                title: 'Find Your Next Adventure',
                subtitle: 'Reserve with TripShock & Save on Top Rated Tours & Activities',
                images: [homeImage2, homeImage2WebP],
            };
        case routes.storiesPage:
            return {
                title: 'Get inspired',
                subtitle: '100% Verified Traveler Stories',
                images: [storiesImage, storiesImageWebP],
            };
        case routes.blogsPage:
            return {
                title: 'Stay Informed',
                subtitle: 'Tripshock has you covered',
                images: [blogsImage, blogsImageWebP],
                forceSimplifyMobile: true,
            };
        case routes.blogPostPage:
            return {
                forceSimplifyMobile: true,
            };
        case routes.itineraryPage:
            return {
                forceSimplifyMobile: true,
            };
        case routes.blogsDestinationPage:
        case routes.destinationPage:
        case routes.storiesDestinationPage:
        case routes.partnersDestinationPage:
            return (() => {
                const substate =
                    state[
                        {
                            [routes.destinationPage]: 'destinationPage',
                            [routes.storiesDestinationPage]: 'storiesDestinationPage',
                            [routes.blogsDestinationPage]: 'blogsDestinationPage',
                            [routes.partnersDestinationPage]: 'partnersDestinationPage',
                        }[path]
                    ];

                const params = substate.params;
                const paramSlug = params.slug.toUpperCase();
                // const paramState = params.state.toUpperCase();
                const destination = destinationsAsObject[paramSlug];

                if (!destination) {
                    return {
                        forceCarousel: true,
                    };
                }

                return {
                    title: `${destination.name}, ${destination.state.name}`,
                    images: [
                        `https://images.tripshock.com/destination/${destination.id}/${destination.slug}.jpg`,
                    ],
                    forceSimplifyMobile: [routes.blogsDestinationPage].includes(path),
                    subtitle: {
                        [routes.partnersDestinationPage]: 'Experience the best',
                        [routes.storiesDestinationPage]: 'Travel Stories',
                        [routes.blogsDestinationPage]: 'Travel Articles',
                        [routes.destinationPage]: (
                            <React.Fragment>
                                Book{' '}
                                {substate.page.isLoaded ? (
                                    substate.page.res.total_activities
                                ) : (
                                    <FontAwesomeIcon spin fixedWidth icon={faSpinner} />
                                )}{' '}
                                Tours, Rentals & Attraction Tickets
                            </React.Fragment>
                        ),
                    }[path],
                };
            })();
        default:
            return defaults;
    }
}

class Header extends React.Component {
    shouldComponentUpdate(nextProps) {
        // Route changed
        if (nextProps.match.path !== this.props.match.path) {
            return true;
        }

        // Common data changed
        if (commonKeys.some((key) => nextProps.common[key] !== this.props.common[key])) {
            return true;
        }

        // Necessary page data has changed
        if (
            pageKeys.some((key) => {
                if (this.props.match.path !== routes[key]) {
                    return false;
                }

                if (!dynamicReducerKeys.includes(key)) {
                    return false;
                }

                if (nextProps.state[key] === this.props.state[key]) {
                    return false;
                }

                return dynamicKeys[key].some(
                    // check state, eq: prev.activityPage.params !== next.activityPage.params
                    (valueKey) => nextProps.state[key][valueKey] !== this.props.state[key][valueKey]
                );
            })
        ) {
            return true;
        }

        return false;
    }

    getSubProps = (props) => {
        return {
            ...defaults,
            ...getSubProps(props),
        };
    };

    render() {
        const {
            common: {
                wishList,
                searchDestinations,
                cartItems,
                searchDestination,
                searchDestinationCategory,
                searchDateFrom,
                searchDateTo,
                checkoutPosition,
            },
            onSelectSearchDestination,
            onSelectSearchDate,
            onSubmitSearch,
            openNavigation,
        } = this.props;

        const subProps = this.getSubProps(this.props);

        if (subProps.unmount) {
            return null;
        }

        return (
            <PureHeader
                destinations={searchDestinations}
                cartItems={cartItems}
                onSelectSearchDestination={onSelectSearchDestination}
                onSelectSearchDate={onSelectSearchDate}
                searchDestination={searchDestination}
                searchDestinationCategory={searchDestinationCategory}
                searchDateFrom={searchDateFrom}
                searchDateTo={searchDateTo}
                onSearchSubmit={onSubmitSearch}
                wishList={wishList}
                id="ts_header"
                key="PureHeader"
                title={subProps.title}
                subtitle={subProps.subtitle}
                images={subProps.images}
                forceSimplifyMobile={subProps.forceSimplifyMobile}
                forceSimplify={subProps.forceSimplify}
                forceCarousel={subProps.forceCarousel}
                navStatic={subProps.navStatic}
                checkoutPosition={checkoutPosition}
                openNavigation={openNavigation}
            />
        );
    }
}

const mapStateToProps = (state) => ({
    state,
    common: createStructuredSelector({
        destinationsAsObject: selectors.app.makeSelectDestinationsAsObject(),
        categoriesAsObject: selectors.app.makeSelectCategoriesAsObject(),
        wishList: selectors.wishList.makeSelectAll(),
        cartItems: selectors.cart.makeSelectItems(),
        searchDestinations: selectors.search.makeSelectDestinations(),
        searchDateFrom: selectors.search.makeSelectDateFrom(),
        searchDateTo: selectors.search.makeSelectDateTo(),
        searchDestination: selectors.search.makeSelectDestination(),
        searchDestinationCategory: selectors.search.makeSelectDestinationCategory(),
        cartIsSad: selectors.cart.makeSelectIsSad(),
        cartIsOutdated: selectors.cart.makeSelectIsOutdated(),
        checkoutPosition: selectors.checkout.makeSelectPosition(),
    })(state),
});

function mapDispatchToProps(dispatch) {
    return {
        onSelectSearchDate: (...args) => dispatch(actions.search.changeDate(...args)),
        onSelectSearchDestination: (...args) => dispatch(actions.search.changeDestination(...args)),
        onSubmitSearch: (...args) => dispatch(actions.search.submit(...args)),
        openNavigation: () => {
            dispatch(actions.navigation.openNavigation());
            AnalyticsInstance.openNavigation();
        },
    };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withRouter, withConnect)(Header);

Header.propTypes = {
    match: PropTypes.shape({
        path: PropTypes.string.isRequired,
    }).isRequired,
};
